import React, { useState } from 'react';
import Icon from '../../components/UI/Icon';
import './ContactPage.css';

const ContactPage = () => {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{success: boolean; message: string} | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      console.log('Sending request with data:', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject || 'Contact Form Submission',
        message: formData.message
      });

      
      const response = await fetch('/contact/send_email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject || 'Contact Form Submission',
          message: formData.message
        })
      });

      console.log('Response status:', response.status);
      
      // Get response text first to handle potential non-JSON responses
      const responseText = await response.text();
      console.log('Raw response:', responseText);
      
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse JSON response:', e);
        throw new Error(`Invalid response from server: ${responseText.substring(0, 100)}...`);
      }
      
      console.log('Parsed response:', result);
      
      if (response.ok && result.success) {
        setSubmitStatus({
          success: true,
          message: result.message || 'Thank you for your message! We will get back to you soon.'
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        const errorMessage = result.message || 
                           (response.statusText || `HTTP Error ${response.status}`);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({
        success: false,
        message: error instanceof Error ? error.message : 'There was an error sending your message. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page" style={{ paddingTop: '80px', marginTop: '-80px' }}>
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <h1>Get in Touch</h1>
          <p>We'd love to hear from you. Reach out to us for any questions or inquiries.</p>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="contact-info-section">
        <div className="container">
          <div className="contact-cards">
            <div className="contact-card">
              <div className="info-item">
                <Icon name="FaMapMarkerAlt" className="info-icon" />
                <div className="info-content">
                  <h3>Our Location</h3>
                  <p>123 Kayamandi Main Road<br />Stellenbosch, 7600<br />Western Cape, South Africa</p>
                </div>
              </div>
              <a 
                href="https://www.google.com/maps/place/Kayamandi,+Stellenbosch,+South+Africa" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-outline"
              >
                View on Map
              </a>
            </div>
            
            <div className="contact-card">
              <div className="info-item">
                <Icon name="FaEnvelope" className="info-icon" />
                <div className="info-content">
                  <h3>Email Us</h3>
                  <p><a href="mailto:info@kwcdp.co.za">info@kwcdp.co.za</a></p>
                  <p><a href="mailto:donations@kwcdp.co.za">donations@kwcdp.co.za</a></p>
                  <p><a href="mailto:partnerships@kwcdp.co.za">partnerships@kwcdp.co.za</a></p>
                </div>
              </div>
              <div className="social-links">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <Icon name="FaFacebookF" />
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                  <Icon name="FaTwitter" />
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <Icon name="FaInstagram" />
                </a>
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                  <Icon name="FaLinkedinIn" />
                </a>
              </div>
            </div>
            
            <div className="contact-card">
              <div className="info-item">
                <Icon name="FaClock" className="info-icon" />
                <div className="info-content">
                  <h3>Opening Hours</h3>
                  <ul className="hours-list">
                    <li><span>Monday - Friday</span> <span>8:00 AM - 5:00 PM</span></li>
                    <li><span>Saturday</span> <span>9:00 AM - 1:00 PM</span></li>
                    <li><span>Sunday</span> <span>Closed</span></li>
                  </ul>
                </div>
              </div>
              <p className="emergency">
                <strong>Emergency Contact:</strong> +27 71 841 6784
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Map Section */}
      <section className="contact-form-section">
        <div className="container">
          <div className="contact-form-container">
            <div className="contact-form-content">
              <div className="section-header">
                <h2>Send Us a Message</h2>
                <p>Fill out the form below and our team will get back to you as soon as possible.</p>
              </div>
              
              {submitStatus && (
                <div className={`form-message ${submitStatus.success ? 'success' : 'error'}`}>
                  {submitStatus.message}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="subject">Subject *</label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select a subject</option>
                      <option value="General Inquiry">General Inquiry</option>
                      <option value="Donation">Donation</option>
                      <option value="Volunteer">Volunteer</option>
                      <option value="Partnership">Partnership</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">Your Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    required
                  ></textarea>
                </div>
                
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
            
            <div className="contact-map">
              <div className="map-container">
                <iframe
                  title="KWCD Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3308.787986352538!2d18.84861531521244!3d-33.93451638062852!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1dcdb5d6b0e7a8a5%3A0x1e3a9b6c1b1b1b1b!2sKayamandi%2C%20Stellenbosch%2C%20South%20Africa!5e0!3m2!1sen!2sus!4v1620000000000!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              </div>
              
              <div className="additional-info">
                <h3>Need Help?</h3>
                <p>If you need immediate assistance, please call our helpline or visit us during business hours.</p>
                <div className="quick-contact">
                  <a href="tel:+27718416784" className="btn btn-outline">
                    <Icon name="FaPhone" /> Call Us Now
                  </a>
                  <a href="mailto:info@kwcdp.co.za" className="btn btn-primary">
                    <Icon name="FaEnvelope" /> Email Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="faq-section bg-light">
        <div className="container">
          <div className="section-header">
            <h2>Frequently Asked Questions</h2>
            <p>Find answers to common questions about KWCD and our programs.</p>
          </div>
          
          <div className="faq-container">
            <div className={`faq-item ${activeFaq === 0 ? 'active' : ''}`}>
              <button 
                className="faq-question"
                onClick={() => toggleFaq(0)}
                aria-expanded={activeFaq === 0}
                aria-controls="faq-answer-0"
              >
                How can I volunteer with KWCD?
                <i className="fas fa-chevron-down"></i>
              </button>
              <div className="faq-answer">
                <p>We are grateful for your interest in volunteering! You can easily get in touch with us to explore opportunities by sending a message through our website's contact page at <a href="https://kwcdp.co.za/contact">kwcdp.co.za/contact</a>. Alternatively, you can send a detailed email expressing your interest and availability to our team at <a href="mailto:info@kwcdp.co.za">info@kwcdp.co.za</a>. Our work relies heavily on community support, and we welcome individuals who are passionate about making a difference in the lives of women and children in Kayamandi.</p>
              </div>
            </div>
            
            <div className={`faq-item ${activeFaq === 1 ? 'active' : ''}`}>
              <button 
                className="faq-question"
                onClick={() => toggleFaq(1)}
                aria-expanded={activeFaq === 1}
                aria-controls="faq-answer-1"
              >
                What types of donations do you accept?
                <i className="fas fa-chevron-down"></i>
              </button>
              <div className="faq-answer">
                <p>We accept a variety of donations to support our mission. You can contribute in the form of clothing, food parcels, and equipment. For monetary donations, you can securely donate through our website's dedicated page at <a href="https://kwcdp.co.za/donate">kwcdp.co.za/donate</a>. If you have non-monetary items you wish to donate or have questions about what we need most, please contact our team directly at <a href="mailto:donations@kwcdp.co.za">donations@kwcdp.co.za</a> or visit our main contact page at <a href="https://kwcdp.co.za/contact">kwcdp.co.za/contact</a>. Every contribution helps us provide essential services to the community.</p>
              </div>
            </div>
            
            <div className={`faq-item ${activeFaq === 2 ? 'active' : ''}`}>
              <button 
                className="faq-question"
                onClick={() => toggleFaq(2)}
                aria-expanded={activeFaq === 2}
                aria-controls="faq-answer-2"
              >
                How can my organization partner with KWCD?
                <i className="fas fa-chevron-down"></i>
              </button>
              <div className="faq-answer">
                <p>Partnering with the Kayamandi Women & Children's Development Project is a great way to amplify our collective impact. To begin, simply send an email to our team outlining your organization's interests and how you envision a partnership. Our team will review your proposal and respond to you within one or two business days to discuss potential collaborations. We are always looking for new opportunities to work with like-minded organizations to create a more supportive and empowering environment for our community members.</p>
              </div>
            </div>
            
            <div className={`faq-item ${activeFaq === 3 ? 'active' : ''}`}>
              <button 
                className="faq-question"
                onClick={() => toggleFaq(3)}
                aria-expanded={activeFaq === 3}
                aria-controls="faq-answer-3"
              >
                How can I get help from your programs?
                <i className="fas fa-chevron-down"></i>
              </button>
              <div className="faq-answer">
                <p>If you or someone you know needs assistance from our programs, we encourage you to get in touch with our operational staff. For general inquiries about our programs and how to access our services, please contact our Project Centre or Program Officer.</p>
                <p>For overall program coordination and day-to-day operations, please reach out to Zimkitha, our Project Centre lead, at <a href="mailto:zimkitha@kwcdp.co.za">zimkitha@kwcdp.co.za</a>.</p>
                <p>For specific inquiries about our community development and educational initiatives, you can contact Lelethu Nelisiwe Jodwana, our Program Officer, at <a href="mailto:lelethu@kwcdp.co.za">lelethu@kwcdp.co.za</a>.</p>
                <p>You can also send a general message to <a href="mailto:info@kwcdp.co.za">info@kwcdp.co.za</a>, and your inquiry will be directed to the appropriate team member.</p>
              </div>
            </div>
          </div>
          
          <div className="cta-box">
            <h3>Still have questions?</h3>
            <p>Contact us directly and our team will be happy to assist you.</p>
            <a href="mailto:info@kwcdp.co.za" className="btn btn-primary">
              <Icon name="FaEnvelope" /> Email Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
